// src/utils/errorReportsApi.ts
import API_BASE_URL from './apiBase';

export type ErrorSeverity = 'fatal' | 'error' | 'warning' | 'info';
export type ErrorStatus = 'open' | 'investigating' | 'resolved' | 'ignored';

export const SEVERITY_OPTIONS: ErrorSeverity[] = ['fatal', 'error', 'warning', 'info'];
export const STATUS_OPTIONS: ErrorStatus[] = ['open', 'investigating', 'resolved', 'ignored'];

export const SEVERITY_COLORS: Record<ErrorSeverity, { bg: string; fg: string }> = {
  fatal:   { bg: '#fee2e2', fg: '#991b1b' },
  error:   { bg: '#fef2f2', fg: '#dc2626' },
  warning: { bg: '#fef3c7', fg: '#92400e' },
  info:    { bg: '#dbeafe', fg: '#1e40af' },
};

export const STATUS_COLORS: Record<ErrorStatus, { bg: string; fg: string }> = {
  open:           { bg: '#fee2e2', fg: '#991b1b' },
  investigating:  { bg: '#fef3c7', fg: '#92400e' },
  resolved:       { bg: '#dcfce7', fg: '#166534' },
  ignored:        { bg: '#f3f4f6', fg: '#6b7280' },
};

export interface ErrorReportSummary {
  id: number;
  app_id: string;
  app_version: string | null;
  platform: string | null;
  severity: ErrorSeverity;
  message: string;
  source: string | null;
  user_id: number | null;
  user_email: string | null;
  http_status: number | null;
  status: ErrorStatus;
  fingerprint: string | null;
  created_at: string;
}

export interface ErrorReportDetail extends ErrorReportSummary {
  stack_trace: string | null;
  http_url: string | null;
  http_method: string | null;
  http_response: string | null;
  context: unknown;
  device_info: unknown;
  notes: string | null;
  resolved_at: string | null;
  resolved_by: number | null;
  ip_address: string | null;
  user_agent: string | null;
}

export interface ErrorAppSummary {
  app_id: string;
  total_count: number;
  open_count: number;
  last_seen: string;
}

export interface ErrorReportFilters {
  appId?: string;
  severity?: ErrorSeverity | 'all';
  status?: ErrorStatus | 'all';
  search?: string;
  limit?: number;
  offset?: number;
}

const authHeaders = (token: string): HeadersInit => ({
  Authorization: `Bearer ${token}`,
});

export const fetchErrorApps = async (token: string): Promise<ErrorAppSummary[]> => {
  const response = await fetch(`${API_BASE_URL}/errors/apps`, { headers: authHeaders(token) });
  if (!response.ok) throw new Error(`Failed to load apps (${response.status})`);
  const data = await response.json();
  return data.apps || [];
};

export const fetchErrorReports = async (
  token: string,
  filters: ErrorReportFilters = {},
): Promise<{ reports: ErrorReportSummary[]; total: number }> => {
  const params = new URLSearchParams();
  if (filters.appId && filters.appId !== 'all') params.set('appId', filters.appId);
  if (filters.severity && filters.severity !== 'all') params.set('severity', filters.severity);
  if (filters.status && filters.status !== 'all') params.set('status', filters.status);
  if (filters.search) params.set('search', filters.search);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.offset) params.set('offset', String(filters.offset));

  const qs = params.toString();
  const response = await fetch(`${API_BASE_URL}/errors${qs ? `?${qs}` : ''}`, {
    headers: authHeaders(token),
  });
  if (!response.ok) throw new Error(`Failed to load reports (${response.status})`);
  const data = await response.json();
  return { reports: data.reports || [], total: data.total || 0 };
};

export const fetchErrorReport = async (token: string, id: number): Promise<ErrorReportDetail> => {
  const response = await fetch(`${API_BASE_URL}/errors/${id}`, { headers: authHeaders(token) });
  if (!response.ok) throw new Error(`Failed to load report (${response.status})`);
  const data = await response.json();
  return data.report;
};

export const updateErrorReport = async (
  token: string,
  id: number,
  patch: { status?: ErrorStatus; notes?: string | null },
): Promise<ErrorReportDetail> => {
  const response = await fetch(`${API_BASE_URL}/errors/${id}`, {
    method: 'PATCH',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!response.ok) throw new Error(`Failed to update report (${response.status})`);
  const data = await response.json();
  return data.report;
};
